import { User } from "lucide-react";
import { cn } from "@/lib/utils";

// Size → [container classes, text classes]
const SIZES = {
  xs: ["h-6 w-6",   "text-[10px]"],
  sm: ["h-8 w-8",   "text-xs"],
  md: ["h-10 w-10", "text-sm"],
  lg: ["h-16 w-16", "text-base"],
};

/**
 * Avatar — unified user avatar with image → initials → icon fallback.
 *
 * Props:
 *   user   object  — convenience: extracts name from user.full_name,
 *                    url from user.avatar_url
 *   name   string  — explicit display name (used for initials)
 *   url    string  — explicit avatar URL
 *   size   "xs"|"sm"|"md"|"lg"   default "md"
 *   ring   bool    — adds ring-2 ring-border   default false
 */
export default function Avatar({ user, name, url, size = "md", ring = false }) {
  const resolvedName = name ?? user?.full_name ?? null;
  const resolvedUrl  = url  ?? user?.avatar_url  ?? null;

  const [sz, textSz] = SIZES[size] ?? SIZES.md;
  const base = cn(sz, "rounded-full shrink-0", ring && "ring-2 ring-border");

  if (resolvedUrl) {
    return (
      <img
        src={resolvedUrl}
        alt={resolvedName ?? ""}
        className={cn(base, "object-cover")}
      />
    );
  }

  if (resolvedName) {
    const initials = resolvedName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
    return (
      <div className={cn(base, textSz, "bg-accent text-accent-foreground font-medium flex items-center justify-center")}>
        {initials}
      </div>
    );
  }

  return (
    <div className={cn(base, "bg-muted flex items-center justify-center")}>
      <User className="h-1/2 w-1/2 text-muted-foreground" />
    </div>
  );
}
